import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { ChatState, ChatThread, ChatMessage, ChatContextValue, ToolCall, InvestmentScore } from '../../types/chat';
import { API_CONFIG, API_ENDPOINTS, getAuthHeaders } from '../../lib/config/api';

const STORAGE_KEY = 'newvaluate-chat-threads';

// Persist threads to localStorage
function saveThreads(threads: ChatThread[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(threads));
  } catch {
    // localStorage full or unavailable
  }
}

// Restore threads from localStorage
function loadThreads(): ChatThread[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatThread[];
    return parsed.map(thread => ({
      ...thread,
      createdAt: new Date(thread.createdAt),
      updatedAt: new Date(thread.updatedAt),
      messages: thread.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
        isStreaming: false,
      })),
    }));
  } catch {
    return [];
  }
}

// Initial state
const restoredThreads = loadThreads();
const initialState: ChatState = {
  threads: restoredThreads,
  currentThreadId: restoredThreads.length > 0
    ? [...restoredThreads].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())[0].id
    : null,
  isLoading: false,
  isStreaming: false,
  error: null,
  selectedHotelId: null,
  selectedHotelName: null,
};

// Actions
type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_STREAMING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_THREADS'; payload: ChatThread[] }
  | { type: 'ADD_THREAD'; payload: ChatThread }
  | { type: 'UPDATE_THREAD'; payload: { threadId: string; messages: ChatMessage[] } }
  | { type: 'DELETE_THREAD'; payload: string }
  | { type: 'RENAME_THREAD'; payload: { threadId: string; title: string } }
  | { type: 'SET_CURRENT_THREAD'; payload: string | null }
  | { type: 'ADD_MESSAGE'; payload: { threadId: string; message: ChatMessage } }
  | { type: 'UPDATE_MESSAGE'; payload: { threadId: string; messageId: string; content: string; isStreaming?: boolean; toolCalls?: ToolCall[]; investmentScore?: InvestmentScore; metadata?: Record<string, unknown> } }
  | { type: 'SELECT_HOTEL'; payload: { hotelId: string; hotelName?: string } }
  | { type: 'CLEAR_HOTEL' }
  | { type: 'CLEAR_CURRENT_THREAD'; payload: string };

// Reducer
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_STREAMING':
      return { ...state, isStreaming: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_THREADS':
      return { ...state, threads: action.payload };

    case 'ADD_THREAD':
      return { ...state, threads: [...state.threads, action.payload] };

    case 'UPDATE_THREAD':
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? { ...thread, messages: action.payload.messages, updatedAt: new Date() }
            : thread
        ),
      };

    case 'DELETE_THREAD':
      return {
        ...state,
        threads: state.threads.filter(thread => thread.id !== action.payload),
        currentThreadId: state.currentThreadId === action.payload ? null : state.currentThreadId,
      };

    case 'RENAME_THREAD':
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? { ...thread, title: action.payload.title, updatedAt: new Date() }
            : thread
        ),
      };

    case 'SET_CURRENT_THREAD':
      return { ...state, currentThreadId: action.payload };

    case 'ADD_MESSAGE':
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                messages: [...thread.messages, action.payload.message],
                updatedAt: new Date(),
              }
            : thread
        ),
      };

    case 'UPDATE_MESSAGE':
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                messages: thread.messages.map(message =>
                  message.id === action.payload.messageId
                    ? {
                        ...message,
                        content: action.payload.content,
                        isStreaming: action.payload.isStreaming ?? message.isStreaming,
                        toolCalls: action.payload.toolCalls ?? message.toolCalls,
                        investmentScore: action.payload.investmentScore ?? message.investmentScore,
                        metadata: {
                          ...message.metadata,
                          ...action.payload.metadata,
                        },
                      }
                    : message
                ),
                updatedAt: new Date(),
              }
            : thread
        ),
      };

    case 'SELECT_HOTEL':
      return {
        ...state,
        selectedHotelId: action.payload.hotelId,
        selectedHotelName: action.payload.hotelName || null,
      };

    case 'CLEAR_HOTEL':
      return { ...state, selectedHotelId: null, selectedHotelName: null };

    case 'CLEAR_CURRENT_THREAD':
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload
            ? { ...thread, messages: [], updatedAt: new Date() }
            : thread
        ),
      };

    default:
      return state;
  }
}

// Context
const ChatContext = createContext<ChatContextValue | null>(null);

// Provider component
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const abortRef = useRef<AbortController | null>(null);

  // Persist threads to localStorage whenever they change
  useEffect(() => {
    saveThreads(state.threads);
  }, [state.threads]);

  // Get current thread
  const currentThread = React.useMemo(() => {
    return state.threads.find(thread => thread.id === state.currentThreadId) || null;
  }, [state.threads, state.currentThreadId]);

  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  // Create new thread
  const createNewThread = useCallback(() => {
    const newThread: ChatThread = {
      id: generateId(),
      title: 'New Chat',
      messages: [
        {
          id: generateId(),
          role: 'assistant',
          content: state.selectedHotelId
            ? `Hello! I'm ready to help you analyze **${state.selectedHotelName || 'your property'}**. What would you like to know?`
            : 'Hello! I\'m your hotel valuation assistant. Ask me about any property in your portfolio, or request a portfolio-wide analysis.',
          timestamp: new Date(),
          metadata: { messageType: 'text' },
        },
      ],
      createdAt: new Date(),
      updatedAt: new Date(),
      hotelId: state.selectedHotelId || undefined,
      hotelName: state.selectedHotelName || undefined,
    };

    dispatch({ type: 'ADD_THREAD', payload: newThread });
    dispatch({ type: 'SET_CURRENT_THREAD', payload: newThread.id });
  }, [state.selectedHotelId, state.selectedHotelName]);

  const selectThread = useCallback((threadId: string) => {
    dispatch({ type: 'SET_CURRENT_THREAD', payload: threadId });
  }, []);

  const deleteThread = useCallback((threadId: string) => {
    dispatch({ type: 'DELETE_THREAD', payload: threadId });
  }, []);

  const renameThread = useCallback((threadId: string, newTitle: string) => {
    dispatch({ type: 'RENAME_THREAD', payload: { threadId, title: newTitle } });
  }, []);

  const clearCurrentThread = useCallback(() => {
    if (state.currentThreadId) {
      dispatch({ type: 'CLEAR_CURRENT_THREAD', payload: state.currentThreadId });
    }
  }, [state.currentThreadId]);

  const selectHotel = useCallback((hotelId: string, hotelName?: string) => {
    dispatch({ type: 'SELECT_HOTEL', payload: { hotelId, hotelName } });
  }, []);

  const clearHotel = useCallback(() => {
    dispatch({ type: 'CLEAR_HOTEL' });
  }, []);

  // Stop streaming immediately by aborting the in-flight request
  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    dispatch({ type: 'SET_STREAMING', payload: false });
  }, []);

  // Send message with SSE streaming
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Abort any in-flight request
    if (abortRef.current) abortRef.current.abort();

    // Ensure we have a current thread
    let targetThreadId = state.currentThreadId;
    if (!targetThreadId) {
      const newThreadId = generateId();
      const newThread: ChatThread = {
        id: newThreadId,
        title: 'New Chat',
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        hotelId: state.selectedHotelId || undefined,
        hotelName: state.selectedHotelName || undefined,
      };
      dispatch({ type: 'ADD_THREAD', payload: newThread });
      dispatch({ type: 'SET_CURRENT_THREAD', payload: newThreadId });
      targetThreadId = newThreadId;
    }

    // User message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date(),
      metadata: {
        hotelId: state.selectedHotelId || undefined,
        hotelName: state.selectedHotelName || undefined,
        messageType: 'text',
      },
    };
    dispatch({ type: 'ADD_MESSAGE', payload: { threadId: targetThreadId, message: userMessage } });

    // Assistant placeholder
    const assistantMessageId = generateId();
    dispatch({
      type: 'ADD_MESSAGE',
      payload: {
        threadId: targetThreadId,
        message: {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isStreaming: true,
          metadata: {
            hotelId: state.selectedHotelId || undefined,
            hotelName: state.selectedHotelName || undefined,
            messageType: 'text',
          },
        },
      },
    });

    dispatch({ type: 'SET_STREAMING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    let headers: Record<string, string>;
    try {
      headers = getAuthHeaders();
    } catch {
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          threadId: targetThreadId,
          messageId: assistantMessageId,
          content: '**Authentication Required**\n\nPlease log in to use the chat feature.',
          isStreaming: false,
        },
      });
      dispatch({ type: 'SET_STREAMING', payload: false });
      return;
    }

    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AGENT_CHAT_STREAM}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({
            message: content,
            hotelId: state.selectedHotelId || undefined,
            conversationId: targetThreadId,
          }),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseContentType = response.headers.get('content-type') || '';

      if (responseContentType.includes('text/event-stream') && response.body) {
        // SSE streaming
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        let finalToolCalls: ToolCall[] | undefined;
        let finalInvestmentScore: InvestmentScore | undefined;
        let finalMetadata: Record<string, unknown> | undefined;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const text = decoder.decode(value, { stream: true });
          const lines = text.split('\n');

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;

            try {
              const chunk = JSON.parse(data);
              if (chunk.error) throw new Error(chunk.error);

              // Accumulate streamed text — backend sends { chunk: "text" }
              const textPart = chunk.chunk || chunk.content || chunk.text || chunk.response;
              if (textPart) {
                accumulated += textPart;
                dispatch({
                  type: 'UPDATE_MESSAGE',
                  payload: {
                    threadId: targetThreadId!,
                    messageId: assistantMessageId,
                    content: accumulated,
                    isStreaming: true,
                  },
                });
              }

              if (chunk.toolCalls?.length) finalToolCalls = chunk.toolCalls;
              if (chunk.investmentScore) finalInvestmentScore = chunk.investmentScore;
              if (chunk.metadata) finalMetadata = chunk.metadata;

              // Final chunk with done:true may carry complete response
              if (chunk.done && chunk.response) {
                accumulated = chunk.response;
                finalToolCalls = chunk.toolCalls;
                finalInvestmentScore = chunk.investmentScore;
                finalMetadata = chunk.metadata;
              }
            } catch {
              // skip malformed chunks
            }
          }
        }

        // Finalize
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            threadId: targetThreadId!,
            messageId: assistantMessageId,
            content: accumulated || 'No response received.',
            isStreaming: false,
            toolCalls: finalToolCalls,
            investmentScore: finalInvestmentScore,
            metadata: finalMetadata,
          },
        });
      } else {
        // JSON fallback
        const json = await response.json();
        if (json.success && json.data) {
          let investmentScore: InvestmentScore | undefined;
          if (json.data.toolCalls) {
            const comp = json.data.toolCalls.find((tc: ToolCall) => tc.name === 'run_comprehensive_analysis');
            const result = comp?.result as Record<string, unknown> | undefined;
            if (result?.investmentScore) investmentScore = result.investmentScore as InvestmentScore;
          }

          dispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              threadId: targetThreadId!,
              messageId: assistantMessageId,
              content: json.data.response,
              isStreaming: false,
              toolCalls: json.data.toolCalls,
              investmentScore,
              metadata: json.data.metadata,
            },
          });
        } else {
          throw new Error(json.error || 'Failed to get response');
        }
      }

      // Auto-title thread from first user message
      const thread = state.threads.find(t => t.id === targetThreadId);
      if (thread && thread.title === 'New Chat' && content.length > 0) {
        const title = content.length > 40 ? content.slice(0, 40) + '...' : content;
        dispatch({ type: 'RENAME_THREAD', payload: { threadId: targetThreadId!, title } });
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;

      const errMsg = (error as Error).message || 'Failed to send message';
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          threadId: targetThreadId!,
          messageId: assistantMessageId,
          content: `Error: ${errMsg}`,
          isStreaming: false,
        },
      });
      dispatch({ type: 'SET_ERROR', payload: errMsg });
    } finally {
      abortRef.current = null;
      dispatch({ type: 'SET_STREAMING', payload: false });
    }
  }, [state.currentThreadId, state.selectedHotelId, state.selectedHotelName, state.threads]);

  // Initialize with default thread if none exist
  useEffect(() => {
    if (state.threads.length === 0) {
      createNewThread();
    }
  }, [createNewThread, state.threads.length]);

  const value: ChatContextValue = {
    state,
    currentThread,
    sendMessage,
    stopStreaming,
    createNewThread,
    selectThread,
    deleteThread,
    renameThread,
    clearCurrentThread,
    selectHotel,
    clearHotel,
    isTyping: state.isStreaming,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
